import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Form, Input, Button, Table, Tabs, Checkbox, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCareTeamAssignableOrganisationUsers } from '@redux/careTeam/actions';
import {
  selectCareTeamAssignableOrganisationUsers,
  selectCareTeamAssignableOrganisationUsersError,
} from '@redux/careTeam/reducers';
import useDirectoryOrganisations from '@hooks/useDirectoryOrganisations';
import { useRuleContentRefs } from '@pathways/hooks/useRuleContentRefs';
import './style.less';

const { TabPane } = Tabs;

const formLayout = {
  layout: 'vertical',
};

const PERMISSION_TYPE_LABELS = {
  send_notification: 'Notify',
  view_data: 'View Data',
  create_data: 'Create Data',
};

function buildPickerRows({
  assignableOrganisationUsers,
  organisationNameBySlug,
  member,
  isMemberDashboardUser,
}) {
  const gatedAssignableRows = (assignableOrganisationUsers || [])
    .filter(user => organisationNameBySlug[user.organisation])
    .map(user => ({
      ...user,
      organisationName: organisationNameBySlug[user.organisation],
    }));

  if (!isMemberDashboardUser || !member?.staffUserId) {
    return gatedAssignableRows;
  }

  const selectedId = String(member.staffUserId);
  if (gatedAssignableRows.some(user => String(user.id) === selectedId)) {
    return gatedAssignableRows;
  }

  const fromAssignable = (assignableOrganisationUsers || []).find(
    user => String(user.id) === selectedId,
  );
  const organisationName =
    organisationNameBySlug[member.staffOrganisationId] || member.businessName || '';

  return [
    {
      id: member.staffUserId,
      organisation: member.staffOrganisationId,
      name: fromAssignable?.name || member.name || '',
      email: fromAssignable?.email || member.email || '',
      phoneNumber: fromAssignable?.phoneNumber || member.phone || '',
      organisationName,
    },
    ...gatedAssignableRows,
  ];
}

function EditMemberModal({ visible, role, onSave, onDelete, onCancel, form, appUserPathway }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getFieldDecorator, validateFields, setFieldsValue, resetFields, isFieldsTouched } = form;

  const hasExistingMember = !!role?.member?.id;
  const isMemberDashboardUser = !!role?.member?.staffUserId;
  const member = role?.member;

  const [activeTab, setActiveTab] = useState('select');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectUserModified, setSelectUserModified] = useState(false);

  const [assignableLoading, assignableOrganisationUsers] = useSelector(
    selectCareTeamAssignableOrganisationUsers,
  );
  const assignableError = useSelector(selectCareTeamAssignableOrganisationUsersError);
  // Subscribe so directory fetch completion re-renders this modal.
  const directoryState = useSelector(state => state.directoryOrganisations);
  const getDirectoryOrganisation = useDirectoryOrganisations();

  const organisationNameBySlug = {};
  let directoryLoading = false;
  const relevantOrganisationSlugs = new Set();
  (assignableOrganisationUsers || []).forEach(user => {
    if (user.organisation) relevantOrganisationSlugs.add(user.organisation);
  });
  if (isMemberDashboardUser && member?.staffOrganisationId) {
    relevantOrganisationSlugs.add(member.staffOrganisationId);
  }
  relevantOrganisationSlugs.forEach(slug => {
    // Kick off directory fetch when needed; read settled state from the subscription.
    getDirectoryOrganisation(slug);
    const loading = directoryState.loading[slug];
    if (loading !== false) {
      directoryLoading = true;
    }
    const organisation = directoryState.byId[slug];
    if (organisation?.organisation_name) {
      organisationNameBySlug[slug] = organisation.organisation_name;
    }
  });

  const tableLoading = assignableLoading || directoryLoading;
  const tableRows = buildPickerRows({
    assignableOrganisationUsers,
    organisationNameBySlug,
    member,
    isMemberDashboardUser,
  });

  const allRules = useMemo(() => {
    if (!appUserPathway?.originalPathway) return [];
    const { indexEvents, stages } = appUserPathway.originalPathway;
    const rules = [];
    (indexEvents || []).forEach(event => rules.push(...(event.rules || [])));
    (stages || []).forEach(stage => rules.push(...(stage.rules || [])));
    return rules;
  }, [appUserPathway]);
  const { rules: hydratedRules } = useRuleContentRefs(allRules);

  useEffect(() => {
    if (!visible) return;
    dispatch(fetchCareTeamAssignableOrganisationUsers());

    setSelectUserModified(false);

    if (!role?.member || isMemberDashboardUser) {
      setActiveTab('select');
    } else {
      setActiveTab('details');
    }

    if (isMemberDashboardUser) {
      setSelectedUserId(String(role.member.staffUserId));
    } else {
      setSelectedUserId(null);
    }

    resetFields();
    if (role?.member && !isMemberDashboardUser) {
      setFieldsValue({
        name: role.member.name || '',
        email: role.member.email || '',
        phone: role.member.phone || '',
        businessName: role.member.businessName || '',
      });
    }
  }, [visible, role]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUserToggle = userId => {
    setSelectedUserId(prev => (prev === userId ? null : userId));
    setSelectUserModified(true);
  };

  const resolveBusinessName = user => {
    const resolvedName = organisationNameBySlug[user.organisation];
    if (resolvedName) {
      return resolvedName;
    }
    if (
      isMemberDashboardUser &&
      member &&
      String(member.staffUserId) === String(user.id) &&
      member.staffOrganisationId === user.organisation
    ) {
      return member.businessName || '';
    }
    return '';
  };

  const handleSave = () => {
    if (activeTab === 'select') {
      if (!selectUserModified && !(isMemberDashboardUser && selectedUserId !== null)) {
        onCancel();
        return;
      }
      if (selectedUserId === null) {
        if (hasExistingMember) {
          onDelete(role);
        } else {
          onCancel();
        }
        return;
      }
      const user = tableRows.find(u => String(u.id) === selectedUserId);
      if (!user) return;
      onSave({
        name: user.name,
        email: user.email,
        phone: user.phoneNumber || '',
        businessName: resolveBusinessName(user),
        staffUserId: Number(user.id),
        staffOrganisationId: user.organisation,
      });
    } else {
      if (!isFieldsTouched()) {
        onCancel();
        return;
      }
      validateFields((err, values) => {
        if (err) return;
        const { name, email, phone, businessName } = values;
        const allEmpty = !name && !email && !phone && !businessName;
        if (allEmpty) {
          if (hasExistingMember) {
            onDelete(role);
          } else {
            onCancel();
          }
          return;
        }
        onSave({
          name,
          email,
          phone,
          businessName,
          staffUserId: null,
          staffOrganisationId: null,
        });
      });
    }
  };

  const allAvailableContent = useMemo(() => {
    const contentMap = new Map();
    hydratedRules.forEach(rule => {
      if (rule.whatDetail) {
        const contentRef = rule.whatDetail.content;
        if (contentRef && !contentMap.has(contentRef)) {
          contentMap.set(contentRef, { ...rule.whatDetail, contentRef });
        }
      }
    });
    return Array.from(contentMap.values());
  }, [hydratedRules]);

  const contentRefMap = useMemo(() => {
    const map = new Map();
    allAvailableContent.forEach(content => {
      if (content.contentRef) map.set(content.contentRef, content);
    });
    return map;
  }, [allAvailableContent]);

  const callToActionMap = useMemo(() => {
    const map = new Map();
    const ctaList = appUserPathway?.originalPathway?.clientConfiguration?.callToAction || [];
    ctaList.forEach(cta => map.set(cta.id, cta));
    return map;
  }, [appUserPathway]);

  const permissionsColumns = [
    {
      title: t('patients:Pathways.careTeam.modal.columns.formName'),
      dataIndex: 'contentRef',
      key: 'itemName',
      ellipsis: true,
      render: (contentRef, record) => {
        if (record.type === 'cta') {
          const cta = callToActionMap.get(record.id);
          return cta?.name || record.id;
        }
        const content = contentRefMap.get(contentRef);
        return content?.name || content?.title || content?.latestVersion?.name || contentRef;
      },
    },
    {
      title: t('patients:Pathways.careTeam.modal.columns.permissionType'),
      dataIndex: 'permissionType',
      key: 'permissionType',
      width: 130,
      render: permissionType => PERMISSION_TYPE_LABELS[permissionType] || permissionType,
    },
  ];

  const permissionsDataSource = (role?.permissions || []).map((permission, index) => ({
    key: `${permission.id}-${index}`,
    ...permission,
  }));

  const dashboardUserColumns = [
    {
      title: '',
      key: 'checkbox',
      width: 40,
      render: (_, user) => (
        <Checkbox
          checked={String(user.id) === selectedUserId}
          onChange={() => handleUserToggle(String(user.id))}
          onClick={e => e.stopPropagation()}
        />
      ),
    },
    {
      title: t('patients:Pathways.careTeam.modal.dashboardUsers.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('patients:Pathways.careTeam.modal.dashboardUsers.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('patients:Pathways.careTeam.modal.assignableOrganisationUsers.organisation'),
      dataIndex: 'organisationName',
      key: 'organisation',
    },
  ];

  return (
    <Modal
      title={t('patients:Pathways.careTeam.modal.title', { roleName: role?.name || '' })}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      className="edit-member-modal"
    >
      {role && (
        <>
          {role.description && <p className="role-description">{role.description}</p>}

          {permissionsDataSource.length > 0 && (
            <div className="role-permissions-section">
              <p className="role-permissions-title">
                {t('patients:Pathways.careTeam.modal.permissionsTitle')}
              </p>
              <Table
                columns={permissionsColumns}
                dataSource={permissionsDataSource}
                pagination={false}
                size="small"
              />
            </div>
          )}

          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab={t('patients:Pathways.careTeam.modal.tabs.selectUser')} key="select">
              {assignableError && (
                <Alert
                  type="error"
                  showIcon
                  message={t('patients:Pathways.careTeam.modal.assignableOrganisationUsersError')}
                  style={{ marginBottom: 12 }}
                />
              )}
              <Table
                columns={dashboardUserColumns}
                dataSource={tableRows}
                rowKey="id"
                pagination={false}
                size="small"
                loading={tableLoading}
                onRow={user => ({
                  onClick: () => handleUserToggle(String(user.id)),
                  style: { cursor: 'pointer' },
                })}
              />
            </TabPane>
            <TabPane tab={t('patients:Pathways.careTeam.modal.tabs.enterDetails')} key="details">
              <Form {...formLayout}>
                <Form.Item label={t('patients:Pathways.careTeam.modal.form.name')}>
                  {getFieldDecorator('name')(<Input type="text" />)}
                </Form.Item>

                <Form.Item label={t('patients:Pathways.careTeam.modal.form.email')}>
                  {getFieldDecorator('email', {
                    rules: [
                      {
                        type: 'email',
                        message: t('patients:Pathways.careTeam.modal.form.emailInvalid'),
                      },
                    ],
                  })(<Input type="email" />)}
                </Form.Item>

                <Form.Item label={t('patients:Pathways.careTeam.modal.form.phone')}>
                  {getFieldDecorator('phone', {
                    rules: [
                      {
                        pattern: /^\+[\d\s]+$/,
                        message: t('patients:Pathways.careTeam.modal.form.phoneInvalid'),
                      },
                    ],
                  })(<Input type="tel" />)}
                </Form.Item>

                <Form.Item label={t('patients:Pathways.careTeam.modal.form.businessName')}>
                  {getFieldDecorator('businessName')(<Input type="text" />)}
                </Form.Item>
              </Form>
            </TabPane>
          </Tabs>

          <div className="modal-footer">
            {hasExistingMember && (
              <Button type="danger" onClick={() => onDelete(role)} style={{ marginRight: 'auto' }}>
                {t('patients:Pathways.careTeam.modal.delete')}
              </Button>
            )}
            <div style={{ marginLeft: 'auto' }}>
              <Button onClick={onCancel} style={{ marginRight: '10px' }}>
                {t('patients:Pathways.careTeam.modal.cancel')}
              </Button>
              <Button type="primary" onClick={handleSave}>
                {t('patients:Pathways.careTeam.modal.save')}
              </Button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}

const WrappedEditMemberModal = Form.create()(EditMemberModal);
export default WrappedEditMemberModal;
